import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    LegacyCityPropType,
    PROVINCE as PROVINCES,
    getProvinceNameMessage,
    CircleSpinner,
    LocationSelector,
    DefaultIntlFormatValues,
    getContactRatehubURL,
    Sizes,
    AnimatedContainer,
    ANIMATION_TYPES,
    ZIndex,
    Form,
    FormComputedAsync,
    FormValue,
    getProvincePrincipalCity,
    timeoutAsPromise,
} from '@ratehub/base-ui';

import { AgentCard } from '@ratehub/mtg-common';

import PAGE_TEMPLATES from '../definitions/PageTemplates';


function MortgageAgentDirectoryTable({
    initialProvinceCode,
    initialCity,
    isTitleDescriptionHidden = true,
    initialAgents = [],
    className,
}) {
    const intl = useIntl();

    const getAgentList = useCallback(
        async ({ location }) => {
            await timeoutAsPromise(DELAY_DURATION); // add a delay to simulate a network request to user

            return filterAgentsForSelectedProvince(initialAgents, location?.province.code);
        },
        [ initialAgents ],
    );

    return (
        <StyledContainer
            data-test-name="mtg-agent-directory-table"
            className={className}
        >
            <FormValue>
                {({ location = getDefaultCity({ city: initialCity, province: initialProvinceCode }) }) => {
                    /*
                    If a CMS author has selected a province, but not a city, we want to display the province name
                    in the title and description. If a CMS author has selected a city as well as a province, we want to display the city in the title and description.
                    If neither are selected, it will default to displaying the user's city in the title and description based on sessionStore data or default to Toronto, ON.
                    */
                    const locationMessage = location.province.code && !location.name
                        ? intl.formatMessage(getProvinceNameMessage(location.province.code))
                        : location.name;

                    return (
                        <If condition={!isTitleDescriptionHidden}>
                            <AnimatedContainer
                                className="animated-container"
                                type={ANIMATION_TYPES.FADE}
                                duration={DELAY_DURATION}
                                withColour={false}
                                isVisible={!!locationMessage}
                                alwaysRenderChildMarkup
                            >
                                <h2
                                    data-test-name="mtg-agent-directory-table-title"
                                    className="showDot rh-title-3xl rh-mb-3"
                                >
                                    <FormattedMessage
                                        id="web-components.agent-directory-table.title"
                                        defaultMessage="{locationName} Mortgage Rates"
                                        values={{
                                            locationName: locationMessage,
                                        }}
                                    />
                                </h2>
                            </AnimatedContainer>
                            <If condition={!locationMessage}>
                                <div className="skeleton skeleton-title rh-border-radius-5px rh-mt-2 rh-mb-3" />
                            </If>

                            <AnimatedContainer
                                className="animated-container"
                                type={ANIMATION_TYPES.FADE}
                                duration={DELAY_DURATION}
                                withColour={false}
                                isVisible={!!locationMessage}
                                alwaysRenderChildMarkup
                            >
                                <p className="rh-text-m">
                                    <FormattedMessage
                                        id="web-components.agent-directory-table.description"
                                        defaultMessage={`
                                To secure the best mortgage rates in {locationName}, contact a mortgage broker or agent in your area.
                                Using a local broker gives you the ability to meet in-person to discuss your needs and
                                concerns, and gives you access to the best mortgage rates offered by banks and lenders across Canada.
                            `}
                                        values={{
                                            locationName: locationMessage,
                                        }}
                                    />
                                </p>
                            </AnimatedContainer>
                            <If condition={!locationMessage}>
                                <div className="skeleton skeleton-text rh-width-100p rh-border-radius-5px rh-mb-0_5" />
                                <div className="skeleton skeleton-text rh-width-100p rh-border-radius-5px rh-mb-0_5" />
                                <div className="skeleton skeleton-text rh-width-100p rh-border-radius-5px rh-mb-0_5" />
                            </If>
                        </If>
                    );
                }}
            </FormValue>

            <div className="location-container rh-display-flex rh-flex-wrap rh-align-items-center rh-justify-content-space-between">
                <p className="location-sub-title rh-title-m rh-my-0">
                    <FormattedMessage
                        id="web-components.agent-directory-table.sub-title"
                        defaultMessage="Get in touch with our mortgage agents today"
                    />
                </p>
                <div className="location-selector-container">
                    <LocationSelector
                        id="mtg-agent-directory-location-selector"
                        name="location"
                        label={(
                            <FormattedMessage
                                id="web-components.agent-directory-table.location-selector-label"
                                defaultMessage="Location"
                            />
                        )}
                        initialProvince={initialProvinceCode}
                        value={initialCity}
                    />
                </div>
            </div>

            <FormComputedAsync
                name="agents"
                nameOfIsFetching="isFetchingAgents"
                nameOfHasFetched="hasFetchedAgents"
                onFetchValue={getAgentList}
                getFormValues={({ location }) => ({ location })}
                initialValue={initialAgents}
                debounce={0} // there's only one input and LocationSelector doesn't need debouncing
            >
                {({ agents = initialAgents, isFetchingAgents, hasFetchedAgents }) => { // TODO: remove default value for agents (https://github.com/ratehub/front-end/issues/9044)
                    const isLoading = isFetchingAgents || !hasFetchedAgents; // we might be displaying agents, but we don't want to drop the spinner until we've fetched them.

                    return (
                        <div className="agent-list-container rh-my-2 rh-position-relative">
                            <If condition={isLoading}>
                                <CircleSpinner zIndex={ZIndex.ELEMENTS} />
                            </If>
                            <Choose>
                                <When condition={agents.length > 0}>
                                    <ul
                                        className="agent-list rh-display-grid rh-p-0"
                                        data-test-name="mtg-agent-list"
                                    >
                                        <For
                                            each="agent"
                                            of={agents.sort(sortAgentsByLastName)}
                                        >
                                            <AgentCard
                                                agent={agent}
                                                key={agent.agentID}
                                            />
                                        </For>
                                    </ul>
                                </When>
                                <Otherwise>
                                    <p className="rh-text-l rh-text-align-center rh-mt-6">
                                        <FormattedMessage
                                            id="web-components.agent-directory-table.no-results"
                                            defaultMessage="At the moment, we don’t know any brokers in this area. {newline}You can still reach out to us on our <contactUs>Contact Us</contactUs> page"
                                            values={{
                                                ...DefaultIntlFormatValues,
                                                contactUs: msg => (
                                                    <a
                                                        href={getContactRatehubURL(intl.locale)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {msg}
                                                    </a>
                                                ),
                                            }}
                                        />
                                    </p>
                                </Otherwise>
                            </Choose>
                        </div>
                    );
                }}
            </FormComputedAsync>
        </StyledContainer>
    );
}

const DELAY_DURATION = 500;

/**
 * I created this function because form won't have values first render, but we don't want to display nothing. We can infer the initial value from the props, or default to Toronto, ON.
 * 
 * @param {{ city, province }} defaults 
 * @returns default city
 */
function getDefaultCity({ city, province = PROVINCES.ONTARIO }) {
    if (city) {
        return city;
    }

    return getProvincePrincipalCity(province);
}

/**
 * 
 * @param {Array<{ provinces: string[] }>} [agents]
 * @param {string} [selectedProvince]
 * @returns {Array}
 */
function filterAgentsForSelectedProvince(agents = [], selectedProvince) {
    // return filtered agents by province
    return agents.filter(currentAgent => currentAgent.provinces.some(({ id }) => id === selectedProvince));
}

/**
 * Sort agents by last name alphabetically
 * 
 * @param {Array<{ lastName: string }>} agents 
 * @returns {Array} sorted agents
 */
function sortAgentsByLastName(a, b) {
    return a.lastName.localeCompare(b.lastName);
}

MortgageAgentDirectoryTable.propTypes = {
    initialProvinceCode: PropTypes.oneOf(Object.values(PROVINCES)),
    initialCity: LegacyCityPropType,
    isTitleDescriptionHidden: PropTypes.bool,
    initialAgents: PropTypes.arrayOf(PropTypes.object),
    className: PropTypes.string,
};

const StyledContainer = styled(Form)`
    > .location-container {
        flex-direction: row;
        gap: ${Sizes.SPACING.TWO};

        .location-sub-title {
            flex-grow: 2;
        }

        .location-selector-container {
            flex: 1 0 auto;
            max-width: 30rem;
            min-width: 20rem;
        }
    }

    > .agent-list-container {
        min-height: 20rem;

        > .agent-list {
            list-style: none;

            grid-template-columns: repeat(auto-fit, minmax(max(18.875rem, (33% - ${Sizes.SPACING.HALF})), 1fr));
            grid-column-gap: ${Sizes.SPACING.HALF};
            grid-row-gap: ${Sizes.SPACING.ONE};
        }
    }

    > .skeleton {
        animation: skeleton-loading 1s linear infinite alternate;

        &.skeleton-title {
            width: 50%;
            height: ${Sizes.SPACING.THREE};
        }

        &.skeleton-text {
            height: ${Sizes.SPACING.ONE_AND_A_QUARTER};
        }
    }

    @keyframes skeleton-loading {
        0% {
            background-color: hsl(200, 20%, 80%);
        }
        100% {
            background-color: hsl(200, 20%, 95%);
        }
    }
`;

MortgageAgentDirectoryTable.blockKey = 'rh/mortgages-agent-brokerage-directory-table';
MortgageAgentDirectoryTable.requiresLayoutRow = true;
MortgageAgentDirectoryTable.supportedPageTypes = [ PAGE_TEMPLATES.mtgAgentsList ];

export default MortgageAgentDirectoryTable;
